import React, { useState } from 'react';

// In-step tool references: a step's instruction text may embed a reusable
// reference to a tool on the document as the token {{tool:ID}} (ID = tool.id).
// These helpers insert, render (as interactive chips), count and plain-text the
// tokens so the same reference works in the editor, the read view, the operate
// kiosk and the PDF.

// Fresh global regex per use — never share a stateful (lastIndex) instance.
const tokenRe = () => /\{\{tool:([^}]+)\}\}/g;

export function toolToken(id) { return `{{tool:${id}}}`; }

// Human summary of a tool, used for the hover title.
export function toolSummary(tool) {
  if (!tool) return 'Tool no longer on this document';
  const bits = [];
  if (tool.documentNumber) bits.push(tool.documentNumber);
  if (tool.metricType) bits.push(`${tool.metricValue || ''} ${tool.metricUnit || ''}`.trim());
  else if (tool.torqueSpec) bits.push(tool.torqueSpec);
  if (tool.calibrationRequired) bits.push('calibrated');
  return bits.length ? `${tool.name} — ${bits.join(' · ')}` : (tool.name || 'Tool');
}

// Insert a tool token into text at a cursor position (returns the new text).
// Adds a separating space before/after the token only when one isn't already
// there, so it never produces double spaces.
export function insertToken(text, cursor, id) {
  const t = text || '';
  const pos = (cursor == null || cursor < 0 || cursor > t.length) ? t.length : cursor;
  const before = t.slice(0, pos);
  const after = t.slice(pos);
  const lead = before && !/\s$/.test(before) ? ' ' : '';
  const trail = after && !/^\s/.test(after) ? ' ' : '';
  return before + lead + toolToken(id) + trail + after;
}

// All tool ids referenced anywhere in the steps.
export function stepsForTool(steps, id) {
  const out = [];
  (steps || []).forEach((s, i) => {
    const re = new RegExp(`\\{\\{tool:${id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}\\}`);
    if (re.test(s.instruction || '')) out.push(s.stepNumber || i + 1);
  });
  return out;
}

// The key facts to surface for a tool, as [label, value] pairs.
function toolFacts(tool) {
  if (!tool) return [];
  const facts = [];
  if (tool.documentNumber) facts.push(['Ref', tool.documentNumber]);
  const src = tool.refType === 'equipment' ? 'Equipment registry'
    : tool.refType === 'jig' ? 'Jig registry' : null;
  if (src) facts.push(['Source', src]);
  const metric = tool.metricValue ? `${tool.metricValue} ${tool.metricUnit || ''}`.trim() : (tool.torqueSpec || '');
  if (metric) facts.push(['Metric', metric]);
  if (tool.calibrationRequired) facts.push(['Calibration', 'required']);
  return facts;
}

// An in-text tool reference: a chip that reveals the tool's key facts on hover.
function ToolChip({ tool, accent }) {
  const [show, setShow] = useState(false);
  const known = !!tool;
  const facts = toolFacts(tool);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 3, padding: '0 6px',
      borderRadius: 10, fontSize: '0.85em', fontWeight: 700, background: accent + '22',
      color: known ? accent : '#94a3b8', whiteSpace: 'nowrap', cursor: 'help' }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      🔧 {known ? tool.name : 'removed tool'}
      {show && (
        <span style={{ position: 'absolute', bottom: '135%', left: 0, zIndex: 60, minWidth: 200, maxWidth: 300,
          background: 'var(--ni-char, #1b1f1e)', border: '1px solid var(--ni-border-soft, #333)', borderRadius: 8,
          padding: '8px 10px', boxShadow: '0 6px 20px rgba(0,0,0,0.45)', whiteSpace: 'normal', cursor: 'default',
          fontWeight: 400, textAlign: 'left' }}>
          <span style={{ display: 'block', fontWeight: 800, fontSize: 12, marginBottom: facts.length ? 5 : 0,
            color: known ? accent : '#94a3b8' }}>{known ? tool.name : 'Tool no longer on this document'}</span>
          {facts.map(([k, v]) => (
            <span key={k} style={{ display: 'block', fontSize: 11, color: 'var(--ni-text-muted, #9aa)' }}>
              {k}: <strong style={{ color: 'var(--ni-text, #e8e8e8)' }}>{v}</strong>
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

// Render instruction text into React nodes, replacing tokens with hover chips.
// tools: the document's tools array (to resolve id → name/facts). accent: chip colour.
export function renderInstruction(text, tools, accent = '#2dd4bf') {
  const t = text || '';
  const byId = {};
  (tools || []).forEach(x => { if (x.id) byId[x.id] = x; });
  const nodes = [];
  let last = 0, m, k = 0;
  const re = tokenRe();
  while ((m = re.exec(t)) !== null) {
    if (m.index > last) nodes.push(t.slice(last, m.index));
    nodes.push(<ToolChip key={`tref-${k++}`} tool={byId[m[1]]} accent={accent} />);
    last = m.index + m[0].length;
  }
  if (last < t.length) nodes.push(t.slice(last));
  return nodes;
}

// Replace tokens with the tool name in plain text (for PDF / non-React contexts).
export function plainInstruction(text, tools) {
  const byId = {};
  (tools || []).forEach(x => { if (x.id) byId[x.id] = x; });
  return (text || '').replace(tokenRe(), (_, id) => {
    const tool = byId[id];
    return tool ? `[${tool.name}]` : '[removed tool]';
  });
}
